import { Icon } from "../Icon"
import { DateTime } from "luxon"
import classNames from "classnames"

import { forwardRef, useRef, ReactNode, useState, useEffect } from "react"
import ReactDatepicker, { ReactDatePickerProps } from "react-datepicker"
import { useIsMounted, ErrorBoundary } from "@oysterhr/common"
import { FormLabel, FormLabelProps } from "../FormLabel"
import { mergeRefs } from "../utils/mergeRefs"
import {
  BASE_CLASSES,
  BASE_DISABLED_CLASSES,
  BASE_ERROR_CLASSES,
  BASE_FOCUS_CLASSES,
} from "./constants"
import { twMerge } from "tailwind-merge"
import { FORM_COMPONENT_FOCUS_CLASSES, FOCUS_STATE_BASE_CLASSES } from "../utils/focusStateClasses"
import { WRAPPER_CLASSES, WRAPPER_MD_CLASSES } from "../utils/formComponentWrapperClasses"
import "react-datepicker/dist/react-datepicker.css"

// TODO: Move this to commons
const convertDateToIsoString = (date: Date): string => {
  const year = date.getFullYear()
  const month = (date.getMonth() + 1).toString().padStart(2, "0") //months are zero based
  const day = date.getDate().toString().padStart(2, "0")
  return `${year}-${month}-${day}`
}

export const STANDARD_DATEPICKER_FORMAT = "yyyy-MM-dd"

type DatePickerProps = Omit<ReactDatePickerProps, "customInputRef" | "onChange"> &
  Omit<FormLabelProps<"label">, "onChange" | "size"> & {
    ariaLabels?: {
      openCalendar?: string
      clearCalendar?: string
    }
    assistiveText?: ReactNode
    error?: string
    onChange: (date: string | null) => void
    placeholder?: string
    clearable?: boolean
    dateFormat?: string
    fullWidth?: boolean
    labelEndAdornment?: ReactNode
  }

// TODO: This is a temporary solution to get the datepicker ref to work
type DatePickerRef = any

/**
 * new Date is required to modify the ISO string so it's in UTC time when passed to the DatePicker
 * @param date string: ISO date string
 * @returns Date
 */
const adjustLocalTimeToUTC = (date: string): Date => {
  // eslint-disable-next-line oyster/frog-forbidden-date
  const dt = new Date(date)
  dt.setMinutes(dt.getMinutes() + dt.getTimezoneOffset())
  return dt
}

/**
 * Please note that we also have StyledLuxonDatePicker, which uses UI library styles and handles react-datepicker timezone issues with Luxon
 * If you need to fix a bug related to timezones, use StyledLuxonDatePicker instead
 *
 * The plan is to eventually build a DatePicker that works with Luxon in @oysterhr/ui and replace the use of `StyledLuxonDatePicker` in the app.
 */
export const DatePicker = forwardRef<DatePickerRef, DatePickerProps>(function DatePicker(
  {
    ariaLabels: customAriaLabels,
    assistiveText,
    label,
    labelEndAdornment,
    value,
    error,
    placeholder,
    isOptional = false,
    disabled = false,
    name,
    className,
    onChange,
    clearable = false,
    dateFormat = STANDARD_DATEPICKER_FORMAT,
    tooltip,
    onBlur,
    onCalendarClose,
    /**
     * Applies w-full class to element
     */
    fullWidth = false,
    ...rest
  },
  ref,
) {
  const internalRef = useRef<DatePickerRef>()
  const ariaLabels: Required<DatePickerProps["ariaLabels"]> = {
    clearCalendar: "clear",
    openCalendar: "open-calendar",
    ...customAriaLabels,
  }

  const datePickerRef = (componentRef: DatePickerRef) => {
    const enhancedRef = !componentRef
      ? componentRef
      : {
          ...componentRef,
          focus: () => componentRef.input.focus(),
        }

    return mergeRefs(ref, internalRef)(enhancedRef)
  }

  return (
    <div
      className={classNames(WRAPPER_CLASSES, WRAPPER_MD_CLASSES, {
        "w-full": fullWidth,
      })}
    >
      {label && (
        <FormLabel
          htmlFor={name}
          id={`${name}-label`}
          label={label}
          assistiveText={assistiveText}
          isOptional={isOptional}
          endAdornment={labelEndAdornment}
          tooltip={tooltip}
        />
      )}
      <div
        onBlur={onBlur}
        tabIndex={-1}
        className={twMerge(
          classNames(BASE_CLASSES, FOCUS_STATE_BASE_CLASSES, FORM_COMPONENT_FOCUS_CLASSES, {
            [BASE_DISABLED_CLASSES]: disabled,
            [BASE_ERROR_CLASSES]: error,
            [BASE_FOCUS_CLASSES]: !error,
          }),
        )}
      >
        <ReactDatePickerErrorBoundary
          clearDate={() => {
            onChange("")
          }}
        >
          <ReactDatepicker
            name={name}
            id={name}
            ariaLabelledBy={`${name}-label`}
            dateFormat={dateFormat}
            disabled={disabled}
            popperPlacement="bottom"
            popperProps={{
              strategy: "fixed",
            }}
            popperModifiers={[
              {
                name: "preventOverflow",
                options: {
                  rootBoundary: "viewport",
                  tether: false,
                  altAxis: false,
                },
              },
            ]}
            wrapperClassName={classNames({ "bg-gray-100": disabled })}
            className={classNames(
              "w-full select-none placeholder-gray-300 outline-none",
              {
                "bg-gray-100": disabled,
              },
              className,
            )}
            ref={datePickerRef as DatePickerRef}
            placeholderText={placeholder}
            selected={value ? adjustLocalTimeToUTC(value) : null}
            popperClassName="react-datepicker-v2"
            onCalendarClose={onCalendarClose}
            onChange={val => {
              if (!val) {
                onChange?.("")
              } else {
                const dt = DateTime.fromJSDate(val)
                if (!dt.isValid || dt.year > 3000) {
                  onChange?.("")
                } else {
                  const newValue = convertDateToIsoString(val as Date)
                  onChange?.(newValue)
                }
              }
            }}
            {...rest}
          />
        </ReactDatePickerErrorBoundary>
        <div className="flex flex-row items-center space-x-2">
          {clearable && value && (
            <Icon
              size="sm"
              onClick={() => onChange?.(null)}
              // TODO: update with i18n
              title={ariaLabels.clearCalendar}
              className="cursor-pointer"
              name="Times"
            />
          )}
          <Icon
            size="sm"
            title={ariaLabels.openCalendar}
            onClick={() => internalRef.current?.input.focus()}
            className="cursor-pointer"
            name="CalendarBlank"
          />
        </div>
      </div>
      {!!error && <div className="text-sm text-error-base">{error}</div>}
    </div>
  )
})

type ReactDatePickerErrorBoundaryProps = {
  clearDate: () => void
  children: ReactNode
  waitForMs?: number
}

export const ReactDatePickerErrorBoundary = ({
  clearDate,
  children,
  waitForMs = 500,
}: ReactDatePickerErrorBoundaryProps) => {
  const isMounted = useIsMounted()
  // Using a key for the error boundary to force it to re-create the component
  // when we recover from it.
  const [key, setKey] = useState(0)
  const timerRef = useRef<null | ReturnType<typeof setTimeout>>(null)

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  const forceRecreateChildren = () => {
    setKey(key + 1)
  }

  const onError = () => {
    clearDate()
    timerRef.current = setTimeout(() => {
      if (isMounted()) {
        forceRecreateChildren()
        clearDate()
      }
    }, waitForMs)
  }
  return (
    <ErrorBoundary key={key} fallbackComponent={<>Invalid Date</>} onError={onError}>
      {children}
    </ErrorBoundary>
  )
}
